import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useMyPayslipsQuery } from '@/client/api/hr/payroll';
import type { Payslip } from '@/client/types/hr';
import { PayslipView } from '../HumanResource/components/PayslipView';
import { useMyPayQuery } from '@/client/api/hr/comp';
import { BONUS_KIND_LABEL, COMP_REASON_LABEL, COMP_STATUS_BADGE } from '@/client/types/hr';
import { EmptyState, Panel, StatusBadge } from '../HumanResource/components/hr-ui';
import { money, pay, salary } from '../HumanResource/components/comp-format';
import { formatDay } from '../HumanResource/components/leave-format';

/** My own pay: approved changes and bonuses only, nothing still under discussion. */
export function PayTab({ name }: { name: string }) {
  const payQ = useMyPayQuery();
  const slipsQ = useMyPayslipsQuery();
  const [viewing, setViewing] = useState<Payslip | null>(null);
  if (payQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-14 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your pay…
      </div>
    );
  }
  if (payQ.isError || !payQ.data) {
    return <EmptyState label={`Could not load your pay: ${(payQ.error as Error)?.message ?? 'unknown error'}`} />;
  }
  const p = payQ.data;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel title="My pay">
        {p.current ? (
          <>
            <p className="text-2xl font-semibold text-gray-900">{salary(p.current)}</p>
            <p className="text-xs text-gray-500">since {formatDay(p.current.effectiveOn)}</p>
          </>
        ) : (
          <p className="text-sm text-gray-500">HR has not recorded your pay yet.</p>
        )}
        {p.scheduled && (
          <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {salary(p.scheduled)} from {formatDay(p.scheduled.effectiveOn)}
          </p>
        )}
      </Panel>

      <Panel title="Pay history">
        {p.changes.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">Nothing yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {p.changes.map((c) => (
              <li key={c.id} className="py-2">
                <p className="text-sm text-gray-900">{pay(c.amount, c.currency, c.frequency)}</p>
                <p className="text-xs text-gray-500">
                  {COMP_REASON_LABEL[c.reason]} · from {formatDay(c.effectiveOn)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Bonuses">
        {p.bonuses.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No bonuses yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {p.bonuses.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-2 py-2">
                <div>
                  <p className="text-sm text-gray-900">{money(b.amount, b.currency)}</p>
                  <p className="text-xs text-gray-500">
                    {BONUS_KIND_LABEL[b.kind]}
                    {b.paidAt ? ` · paid ${formatDay(b.paidAt)}` : b.payableOn ? ` · due ${formatDay(b.payableOn)}` : ''}
                  </p>
                </div>
                <StatusBadge label={b.status === 'paid' ? 'Paid' : 'Approved'} className={COMP_STATUS_BADGE[b.status]} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Payslips" className="lg:col-span-3">
        {slipsQ.isLoading ? (
          <p className="text-sm text-gray-500">Loading payslips…</p>
        ) : (slipsQ.data ?? []).length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">No payslips yet. They appear once a pay run is approved.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {(slipsQ.data ?? []).map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                <button type="button" className="flex items-center gap-2 text-sm text-gray-800 hover:text-blue-700" onClick={() => setViewing(p)}>
                  <FileText className="h-4 w-4 text-gray-400" />
                  {p.period ? new Date(`${p.period}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }) : ''}
                </button>
                <span className="text-sm text-gray-900">
                  {money(p.net, p.currency)} <span className="text-xs text-gray-400">{p.runStatus === 'paid' ? 'paid' : 'approved, to be paid'}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Payslip</DialogTitle>
          </DialogHeader>
          {viewing && <PayslipView slip={viewing} name={name} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
